import React from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../AuthProvider'
import { getActiveOrgId } from '../../utils/orgUtils'
import { fetchBlastRadius } from '../../services/informationSystemPhase16Api'
import styles from '../../styles/components/assets/AssetsRiskCompliance.module.scss'

type Tab = 'vulnerabilities' | 'controls' | 'evidence' | 'remediation'

export default function BlastRadiusPanel() {
  const { t } = useI18n()
  const { token } = useAuth()
  const orgId = getActiveOrgId()
  const [tab, setTab] = React.useState<Tab>('vulnerabilities')
  const [data, setData] = React.useState<Record<string, unknown> | null>(null)
  const [loading, setLoading] = React.useState(false)

  React.useEffect(() => {
    if (!token) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        const res = await fetchBlastRadius(token, tab, orgId)
        if (!cancelled) setData(res)
      } catch {
        if (!cancelled) setData(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [token, orgId, tab])

  const impacts =
    (data?.impacts as Array<Record<string, unknown>>) ||
    (data?.systemsWithGaps as Array<Record<string, unknown>>) ||
    (data?.blockers as Array<Record<string, unknown>>) ||
    []

  return (
    <div className={styles.intelligenceCard}>
      <div className={styles.cardHeader}>
        <h3 className={styles.cardTitle}>{t('assets.workflows.blastRadiusTitle', 'Blast radius analysis')}</h3>
        <div className={styles.tabPills}>
          {(['vulnerabilities', 'controls', 'evidence', 'remediation'] as Tab[]).map((k) => (
            <button
              key={k}
              type="button"
              className={`${styles.tabPill} ${tab === k ? styles.tabPillActive : ''}`.trim()}
              onClick={() => setTab(k)}
            >
              {t(`assets.workflows.blastRadiusTab.${k}`, k)}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.cardBody}>
        {loading ? <p className={styles.loadingText}>{t('common.loading', 'Loading…')}</p> : null}
        {!loading && impacts.length === 0 ? (
          <p className={styles.widgetEmpty}>{t('assets.workflows.noBlastRadius', 'No cross-system impact patterns.')}</p>
        ) : null}
        {!loading && impacts.length > 0 ? (
          <ul className={styles.correlationList}>
            {impacts.slice(0, 12).map((item, i) => {
              const label = String(item.cve || item.controlId || item.title || item.name || item.impactSummary || `#${i + 1}`)
              const systems = (item.affectedSystems as Array<{ name?: string }>) || []
              return (
                <li key={i} className={styles.correlationItem}>
                  <p className={styles.correlationItemTitle}>{label}</p>
                  <p className={styles.correlationItemMeta}>{String(item.explanation || item.impactSummary || '')}</p>
                  {systems.length > 0 ? (
                    <p className={styles.correlationItemMeta}>{systems.map((s) => s.name).filter(Boolean).join(', ')}</p>
                  ) : null}
                </li>
              )
            })}
          </ul>
        ) : null}
      </div>
    </div>
  )
}
